import { Fragment } from "react";
import { getStore } from "@store";
//actions
import {
  setTabErrors,
  setError,
  clearErrors,
  setAlertDock,
} from "@actions/global";
//helpers
import { formatErrors } from "@features/sypi/_utils/sypi-interface-helpers";
import { isEmptyObject } from "@utils/object-helpers";
import Ajv from "ajv";
import addFormats from "ajv-formats";
import cloneDeep from "lodash/cloneDeep";
import get from "lodash/get";
import { getSchemaError } from "./schemaManagementInterface";
import { BrcPhoneScreenHeader } from "../utils/smUiHelpers";
import { mergeAdvanced } from "object-merge-advanced";
//constants
import { SYPI_ERROR_TYPES } from "@constants";
import { SYPI_BRC_SECTIONS } from "@constants/schema-management";

export const dispatchObjErrors = (
  configToValidate,
  configSchema,
  release_version = "",
  store = getStore()
) => {
  if (
    !Object.keys(configToValidate).some(
      (key) => Object.keys(configToValidate?.[key]).length === 0
    ) ||
    !store
  ) {
    const entireValidation = new Ajv({
      // errorDataPath: "property",
      allErrors: true,
      multipleOfPrecision: 8,
      // unknownFormats: "ignore",
      // jsonPointers: true,
    });
    addFormats(entireValidation);
    const copyConfigToValidate = cloneDeep(configToValidate);
    const validatedWhole = entireValidation.validate(
      configSchema,
      copyConfigToValidate
    );
    let fullErrorReport = {
      schemaErrors: [],
      configMissingKeys: [],
      hasMinLenght: [],
      otherErrors: [],
    };
    let configSchemaError = {},
      configValidatedErrors = [];
    if (!validatedWhole) {
      const { errors } = entireValidation;
      for (let schemaError in errors) {
        let path = errors[schemaError].instancePath
            .substring(1)
            .replace(/\//g, "."),
          subNestedPath;
        if (
          errors[schemaError].keyword ===
            SYPI_ERROR_TYPES.ADDITIONAL_PROPERTIES ||
          errors[schemaError].keyword === SYPI_ERROR_TYPES.TYPE ||
          errors[schemaError].keyword === SYPI_ERROR_TYPES.MINIMUM_PROPERTIES
        ) {
          configSchemaError = {
            ...configSchemaError,
            ...getSchemaError(errors[schemaError]),
          };
        } else if (
          errors[schemaError].keyword === SYPI_ERROR_TYPES.MINIMUM_LENGTH
        ) {
          configValidatedErrors.push(path);
          fullErrorReport.hasMinLenght.push(
            `${errors[schemaError].message} ${path}`
          );
        } else if (path.length) {
          subNestedPath = Object.values(errors[schemaError].params)[0];
          let finalMessage = `${subNestedPath} ${errors[schemaError].message} ${path}.${subNestedPath}`;
          configValidatedErrors.push(`${path}.${subNestedPath}`);
          if (errors[schemaError].keyword === SYPI_ERROR_TYPES.REQUIRED) {
            fullErrorReport.configMissingKeys.push(finalMessage);
          } else {
            fullErrorReport.otherErrors.push(finalMessage);
          }
        }
      }
      fullErrorReport.schemaErrors = Object.keys(configSchemaError).map(
        (schemaError) => configSchemaError[schemaError].customMessage
      );
    }

    if (store) {
      sendGlobalBrcError(
        configSchemaError,
        configValidatedErrors,
        release_version
      );
    }
    return {
      fullErrorReport,
      configSchemaError,
      errors: entireValidation.errors,
    };
  }
};
const sendGlobalBrcError = (
  configSchemaError,
  configErrors,
  release_version,
  store = getStore()
) => {
  const { activeReleaseVersion, initialReleaseVersion } =
    store.getState().schema;
  const releaseVersion = release_version
    ? release_version
    : initialReleaseVersion;

  if (
    configSchemaError &&
    !isEmptyObject(configSchemaError) &&
    releaseVersion === activeReleaseVersion
  ) {
    store.dispatch(setError(configSchemaError));
    store.dispatch(
      setAlertDock({
        title: "Schema Config Sync Error",
        message: (
          <Fragment>
            The following errors in the configuration file {releaseVersion} have
            a conflict with schema validation: <br />
            {Object.values(configSchemaError).map((error) => (
              <b key={error.fullPath}>
                {`${error.customMessage}.`} <br />
              </b>
            ))}
            Please contact the application development team for assistance.
          </Fragment>
        ),
      })
    );
  } else {
    store.dispatch(setError({}));
    store.dispatch(clearErrors());
  }
  if (configErrors.length) {
    store.dispatch(setTabErrors(formatErrors(configErrors)));
  } else {
    store.dispatch(setTabErrors({}));
  }
};
export const buildBrcPhoneConfig = (store = getStore()) => {
  const { schemaManagementConfig } = store.getState().schemaAllView;
  const content = schemaManagementConfig?.content?.apply;
  const darkMode = schemaManagementConfig?.features?.darkMode;
  const schemaManagementConfigStyles = schemaManagementConfig?.styles;
  const colorScheme = {
    paletteLight: schemaManagementConfigStyles?.paletteLight,
    paletteDark: schemaManagementConfigStyles?.paletteDark,
    textLight: schemaManagementConfigStyles?.textLight,
    textDark: schemaManagementConfigStyles?.textDark,
  };
  const tabs = Object.keys(content?.generalNavigation || {})
    .filter((key) => key.includes("Progress"))
    .map((key) => {
      return {
        value: key.split("Progress")[0].toLowerCase(),
        label: content.generalNavigation[key].value,
      };
    });
  if (!tabs.length) {
    return {};
  }
  const tabsConfig = tabs.reduce((config, currentTab) => {
    const navigationSection =
      content.generalNavigation.sectionNavigation?.[currentTab.value];
    if (navigationSection) {
      config[currentTab.value] = {
        navigationSection: {
          component: "Select",
          sections: Object.keys(navigationSection).map((key) => {
            return {
              label: navigationSection[key].value,
              key: key,
              fields: getFields(key),
            };
          }),
        },
      };
    } else {
      config[currentTab.value] = { fields: getFields(currentTab.value) };
    }
    return config;
  }, {});

  const phoneConfig = {
    Header: ({ style }) => {
      return (
        <BrcPhoneScreenHeader
          details={content?.generalNavigation?.offerDetails?.subtitle1?.value}
          style={style}
        />
      );
    },
    Tabs: tabs,
    TabsConfig: tabsConfig,
    Styles: {
      tabsStyle:
        "text-dark-gray w-16 cursor-pointer py-4 text-center border-b-4 border-b-dark-gray mr-1",
      selectedTabStyle: "border-b-4 border-b-sky-900",
      inputFieldStyle:
        " !bg-transparent border-b-2 border-b-disabled !cursor-no-drop rounded-md p-2",
    },
    ColorScheme: colorScheme,
    darkModeOn: darkMode,
  };
  return phoneConfig;
};

const getFields = (page, store = getStore()) => {
  const { schemaManagementConfig } = store.getState().schemaAllView;
  const content = schemaManagementConfig?.content?.apply;
  let fields = [];
  let section = SYPI_BRC_SECTIONS[page] || page;

  if (section && get(content, section)) {
    const contentSection = get(content, section);

    fields = Object.keys(contentSection).flatMap((key) => {
      let component;
      if (typeof contentSection[key] === "object" && contentSection[key]?.value === undefined) {
        return getFields(`${section}.${key}`);
      } else if (key.includes("Helper")) {
        component = "Helper";
      } else if (key.includes("Button")) {
        component = "Button";
      } else if (key.includes("Text")) {
        component = "Text";
      } else {
        component = "InputField";
      }
      return {
        key,
        value: get(content, `${section}.${key}`)?.value,
        component,
      };
    });
  }
  return fields;
};

export const formatBrcConfig = (schemaManagementConfig, formData) => {
  const mergedConfig = mergeAdvanced(schemaManagementConfig, formData, {
    cb: (inputArg1, inputArg2, resultAboutToBeReturned) =>
      (Array.isArray(inputArg1) &&
        Array.isArray(inputArg2) &&
        inputArg2.length) ||
      typeof inputArg2 === "boolean" ||
      (typeof inputArg2 === "string" && !inputArg2)
        ? inputArg2
        : resultAboutToBeReturned,
  });
  return mergedConfig;
};
